import {
  type ActivityKind,
  dayMon,
  firstName,
  formatMoney,
  initials,
  localTime12,
  PAYMENT_METHOD_LABEL,
  PAYMENT_METHODS,
  type PaymentDto,
  type PaymentMethod,
  type PaymentStatus,
  type StudentDto,
  shortDate,
} from '@hudhuria/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { z } from 'zod';
import { useTodayView } from '~/api/derived';
import { usePhotoMutation, useSettleMutation } from '~/api/mutations';
import { usePendingSettles } from '~/api/pending';
import { profileQuery, qk, useCan, useRoster } from '~/api/queries';
import { CameraIcon } from '~/components/icons';
import {
  Avatar,
  AvatarDot,
  BackLink,
  BottomSheet,
  Chip,
  EmptyState,
  Overlay,
  OverlayFooter,
  PrimaryButton,
  SegmentedControl,
  Spinner,
} from '~/components/ui';
import { api, ok } from '~/lib/api';
import { fileToPhotoDataUrl } from '~/lib/image';
import { useBack } from '~/lib/nav';
import { toast } from '~/lib/toast';
import { useOnline } from '~/offline/online';

export const Route = createFileRoute('/_app/student/$studentId')({
  validateSearch: z.object({ tab: z.enum(['visits', 'pay', 'contacts']).optional() }),
  component: Profile,
});

const PAY_LABEL: Record<PaymentStatus, { label: string; fg: string }> = {
  free: { label: 'Free', fg: '#94a3b8' },
  paid: { label: 'Paid', fg: '#047857' },
  later: { label: 'Pay later', fg: '#b45309' },
  waived: { label: 'Waived', fg: '#94a3b8' },
};
const KIND_DOT: Record<ActivityKind, string> = {
  Attendance: '#0369a1',
  Workshop: '#10b981',
  Event: '#ec4899',
  Exam: '#8b5cf6',
};

function Profile() {
  const { studentId } = Route.useParams();
  const { tab = 'visits' } = Route.useSearch();
  const navigate = useNavigate();
  const back = useBack('/students');
  const view = useTodayView();
  const roster = useRoster();
  const canPayments = useCan('payments');
  const canCards = useCan('idcards');
  // changing the photo of an existing student, and deleting them, is for admins
  const canManage = useCan('manageStudents');
  const photo = usePhotoMutation();
  const settles = usePendingSettles();
  const [settling, setSettling] = useState<PaymentDto | null>(null);
  const [deleting, setDeleting] = useState(false);

  const cached = roster.data?.find((s) => s.id === studentId);
  const profile = useQuery({ ...profileQuery(studentId), enabled: !!cached && !cached.pending });
  const s = cached ?? profile.data?.student;

  if (!s) {
    return (
      <Overlay>
        <div className="px-5 pt-safe-4">
          <BackLink label="Back" onClick={back} />
        </div>
        {roster.isPending ? (
          <div className="flex flex-1 items-center justify-center">
            <Spinner size={26} />
          </div>
        ) : (
          <EmptyState>Student not found.</EmptyState>
        )}
      </Overlay>
    );
  }

  const isIn = view.present.has(s.id);
  const status = isIn
    ? { label: 'In today', bg: '#ecfdf5', fg: '#047857' }
    : s.owes
      ? { label: 'Balance due', bg: '#fef3c7', fg: '#b45309' }
      : { label: 'Not in', bg: '#f3f5f9', fg: '#64748b' };
  const settledIds = new Set(settles.map((x) => x.paymentId));
  const tabs = canPayments
    ? [
        { value: 'visits' as const, label: 'Visits' },
        { value: 'pay' as const, label: 'Payments' },
        { value: 'contacts' as const, label: 'Guardian' },
      ]
    : [
        { value: 'visits' as const, label: 'Visits' },
        { value: 'contacts' as const, label: 'Guardian' },
      ];
  const current = tab === 'pay' && !canPayments ? 'visits' : tab;
  const hasGuardian = s.guardianName && s.guardianName !== '—';
  const phone = s.guardianPhone && s.guardianPhone !== '—' ? s.guardianPhone : '';

  return (
    <Overlay>
      <div className="shrink-0 px-5 pt-safe-4 pb-[18px]">
        <div className="flex items-center justify-between">
          <BackLink label="Back" onClick={back} />
          {canCards && !s.pending && (
            <button
              type="button"
              onClick={() => void navigate({ to: '/id-cards', search: { student: s.id } })}
              className="cursor-pointer border-none bg-transparent p-0 text-[13.5px] font-semibold text-brand"
            >
              ID card
            </button>
          )}
        </div>
        <div className="mt-4 flex items-center gap-[15px]">
          {/* biome-ignore lint/a11y/noLabelWithoutControl: the file input is inside when the viewer can change photos */}
          <label className={`relative shrink-0 ${canManage ? 'cursor-pointer' : ''}`}>
            <Avatar name={s.name} photoUrl={s.photoUrl} size={76} radius={22} fontSize={28} display>
              <AvatarDot color={isIn ? '#10b981' : '#cbd5e1'} size={20} border={3} offset={-4} />
            </Avatar>
            {canManage && (
              <>
                {!s.photoUrl && (
                  <div className="absolute -top-1 -left-1 flex h-6 w-6 items-center justify-center rounded-full border-2 border-white bg-ink text-white">
                    <CameraIcon size={12} />
                  </div>
                )}
                <input
                  type="file"
                  accept="image/*"
                  capture="environment"
                  className="hidden"
                  onChange={async (e) => {
                    const file = e.target.files?.[0];
                    e.target.value = '';
                    if (!file) return;
                    try {
                      photo.mutate({ studentId: s.id, dataUrl: await fileToPhotoDataUrl(file) });
                      toast('Photo saved');
                    } catch {
                      toast("Couldn't read that photo");
                    }
                  }}
                />
              </>
            )}
          </label>
          <div className="min-w-0">
            <div className="font-display text-[22px] font-bold tracking-[-0.01em]">{s.name}</div>
            <div className="mt-0.5 text-[13px] text-slate-500">{s.school}</div>
            <div className="mt-2 flex items-center gap-1.5">
              <span className="rounded-[20px] bg-brand-50 px-[10px] py-1 font-display text-[12px] font-bold text-brand">
                {s.ref}
              </span>
              <span
                className="rounded-[20px] px-[10px] py-1 text-[12px] font-semibold"
                style={{ background: status.bg, color: status.fg }}
              >
                {status.label}
              </span>
            </div>
          </div>
        </div>
        <div className="mt-[18px] grid grid-cols-3 gap-2">
          <Stat value={s.visits} label="Visits" />
          <Stat value={s.paidEvents} label="Paid events" />
          <Stat
            value={s.ltvMinor == null ? '—' : formatMoney(s.ltvMinor, view.currency)}
            label="Lifetime value"
            brand
          />
        </div>
        <SegmentedControl
          className="mt-4"
          value={current}
          onChange={(t) =>
            void navigate({ to: '.', search: { tab: t === 'visits' ? undefined : t }, replace: true })
          }
          options={tabs}
        />
      </div>

      <div className="flex-1 px-5 pb-4 scroll-y">
        {s.pending && <EmptyState>History appears once this registration syncs.</EmptyState>}
        {!s.pending && profile.isPending && (
          <div className="flex justify-center py-8">
            <Spinner />
          </div>
        )}
        {!s.pending && profile.isError && !profile.data && (
          <EmptyState>History is available when you're online.</EmptyState>
        )}

        {current === 'visits' &&
          profile.data?.visits.map((v) => {
            const { day, mon } = dayMon(v.occurredAt, view.tz);
            const pay = PAY_LABEL[v.paymentStatus];
            return (
              <div key={v.id} className="flex items-center gap-3 border-b border-line-faint py-3">
                <div className="w-11 shrink-0 text-center">
                  <div className="font-display text-[17px] leading-none font-bold">{day}</div>
                  <div className="mt-0.5 text-[10px] tracking-[0.05em] text-slate-400">{mon}</div>
                </div>
                <div className="min-w-0 flex-1">
                  <div className="truncate text-[14px] font-semibold">{v.activityName}</div>
                  <div className="mt-px truncate text-[12px] text-slate-400">
                    {localTime12(v.occurredAt, view.tz)} · {v.how}
                  </div>
                </div>
                <div className="text-right">
                  <div
                    className="font-display text-[14px] font-bold"
                    style={{ color: v.paymentStatus === 'paid' ? '#0f172a' : '#cbd5e1' }}
                  >
                    {v.feeMinor ? formatMoney(v.feeMinor, view.currency) : '—'}
                  </div>
                  <div className="mt-0.5 text-[10.5px] font-bold" style={{ color: pay.fg }}>
                    {pay.label}
                  </div>
                </div>
              </div>
            );
          })}
        {current === 'visits' && profile.data && profile.data.visits.length === 0 && (
          <EmptyState>No visits yet.</EmptyState>
        )}

        {current === 'pay' && profile.data?.payments && (
          <div className="flex flex-col gap-[9px]">
            {profile.data.payments.map((p) => {
              const outstanding = p.status === 'outstanding' && !settledIds.has(p.id);
              return (
                <button
                  type="button"
                  key={p.id}
                  disabled={!outstanding}
                  onClick={() => setSettling(p)}
                  className="flex cursor-pointer items-center gap-3 rounded-[14px] border border-line bg-white px-[14px] py-[13px] text-left disabled:cursor-default"
                >
                  <div
                    className="h-[9px] w-[9px] shrink-0 rounded-[3px]"
                    style={{ background: outstanding ? '#f59e0b' : KIND_DOT[p.activityKind ?? 'Attendance'] }}
                  />
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-[14px] font-semibold">{p.label}</div>
                    <div className="mt-px text-[12px] text-slate-400">
                      {shortDate(p.date, view.tz)} · {p.receiptNo}
                      {p.method && ` · ${PAYMENT_METHOD_LABEL[p.method]}`}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-display text-[14.5px] font-bold">
                      {formatMoney(p.amountMinor, view.currency)}
                    </div>
                    <div
                      className="mt-0.5 text-[10.5px] font-bold"
                      style={{ color: outstanding ? '#b45309' : '#047857' }}
                    >
                      {outstanding
                        ? 'Outstanding · tap to settle'
                        : settledIds.has(p.id)
                          ? 'Paid · syncing'
                          : 'Paid'}
                    </div>
                  </div>
                </button>
              );
            })}
            {profile.data.payments.length === 0 && <EmptyState>No payments yet.</EmptyState>}
          </div>
        )}

        {current === 'contacts' &&
          (hasGuardian || phone ? (
            <div className="rounded-2xl border border-line p-[14px]">
              <div className="flex items-center gap-3">
                <div className="flex h-[42px] w-[42px] items-center justify-center rounded-[13px] bg-brand-50 text-[14px] font-bold text-brand">
                  {initials(hasGuardian ? s.guardianName : 'G')}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="truncate text-[14.5px] font-semibold">
                    {hasGuardian ? s.guardianName : 'Guardian'}
                  </div>
                  <div className="mt-px text-[12px] text-slate-400">
                    Primary guardian · {phone || 'no phone'}
                  </div>
                </div>
              </div>
              {phone && (
                <div className="mt-3 flex gap-2">
                  <a
                    href={`tel:${phone}`}
                    className="flex-1 rounded-[11px] bg-brand p-[10px] text-center text-[13px] font-semibold text-white no-underline hover:text-white"
                  >
                    Call
                  </a>
                  <a
                    href={`sms:${phone}`}
                    className="flex-1 rounded-[11px] bg-fill p-[10px] text-center text-[13px] font-semibold text-ink no-underline hover:text-ink"
                  >
                    Message
                  </a>
                </div>
              )}
            </div>
          ) : (
            <EmptyState>No guardian details on file.</EmptyState>
          ))}

        {canManage && !s.pending && (
          <button
            type="button"
            onClick={() => setDeleting(true)}
            className="mt-6 w-full cursor-pointer border-none bg-transparent py-2 text-[13.5px] font-semibold text-red-600"
          >
            Delete student
          </button>
        )}
      </div>

      {!isIn && (
        <OverlayFooter>
          <PrimaryButton
            className="p-[15px] text-[15px]"
            onClick={() =>
              void navigate({
                to: '/checkin/$studentId',
                params: { studentId: s.id },
                search: { from: `/student/${s.id}` },
              })
            }
          >
            Check in {firstName(s.name)}
          </PrimaryButton>
        </OverlayFooter>
      )}

      {deleting && <DeleteStudentSheet student={s} onClose={() => setDeleting(false)} />}

      {settling && (
        <SettleSheet
          payment={settling}
          studentId={s.id}
          currency={view.currency}
          onClose={() => setSettling(null)}
        />
      )}
    </Overlay>
  );
}

function Stat({ value, label, brand }: { value: string | number; label: string; brand?: boolean }) {
  return (
    <div className={`rounded-[14px] p-3 text-center ${brand ? 'bg-brand-50' : 'bg-fill-soft'}`}>
      <div className={`truncate font-display text-[20px] font-bold ${brand ? 'text-brand' : ''}`}>
        {value}
      </div>
      <div className={`mt-0.5 text-[11px] ${brand ? 'text-brand' : 'text-slate-500'}`}>{label}</div>
    </div>
  );
}

/** Record an outstanding fee as paid (no gateway — the method is noted for the books). */
function SettleSheet({
  payment,
  studentId,
  currency,
  onClose,
}: {
  payment: PaymentDto;
  studentId: string;
  currency: string;
  onClose: () => void;
}) {
  const settle = useSettleMutation();
  const [method, setMethod] = useState<PaymentMethod>(currency === 'KES' ? 'mpesa' : 'cash');
  return (
    <BottomSheet onClose={onClose}>
      <div className="font-display text-[20px] font-bold">Record payment</div>
      <div className="mt-1 text-[13.5px] text-slate-500">
        {payment.label} · {payment.receiptNo}
      </div>
      <div className="mt-4 rounded-2xl bg-fill-soft p-4 text-center">
        <div className="font-display text-[34px] font-bold">{formatMoney(payment.amountMinor, currency)}</div>
        <div className="text-[12px] text-slate-500">outstanding</div>
      </div>
      <div className="mt-4 mb-2 text-[12.5px] font-semibold text-slate-600">Paid with</div>
      <div className="flex gap-[7px]">
        {PAYMENT_METHODS.map((m) => (
          <Chip
            key={m}
            on={method === m}
            onClick={() => setMethod(m)}
            className="flex-1 py-[10px] text-[13px]"
          >
            {PAYMENT_METHOD_LABEL[m]}
          </Chip>
        ))}
      </div>
      <PrimaryButton
        className="mt-5"
        onClick={() => {
          settle.mutate({ paymentId: payment.id, studentId, method });
          toast(`${formatMoney(payment.amountMinor, currency)} recorded · ${PAYMENT_METHOD_LABEL[method]}`);
          onClose();
        }}
      >
        Mark as paid
      </PrimaryButton>
    </BottomSheet>
  );
}

/**
 * Admins only. The student leaves the roster, groups, counts and check-in (their ID card stops
 * working); past visits and payments stay in the Excel reports. Needs a connection: it isn't queued.
 */
function DeleteStudentSheet({ student, onClose }: { student: StudentDto; onClose: () => void }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const online = useOnline();
  const first = firstName(student.name);
  const remove = useMutation({
    mutationFn: () => ok<void>(api.students[':id'].$delete({ param: { id: student.id } })),
    onSuccess: () => {
      qc.setQueryData<StudentDto[]>(qk.students, (old) => old?.filter((x) => x.id !== student.id));
      qc.removeQueries({ queryKey: qk.student(student.id) });
      for (const key of [qk.students, qk.me, ['today'], ['groups']])
        void qc.invalidateQueries({ queryKey: key });
      toast(`${student.name} deleted`);
      void navigate({ to: '/students', replace: true });
    },
    onError: (e) => toast(e.message),
  });

  return (
    <BottomSheet onClose={onClose}>
      <div className="font-display text-[20px] font-bold">Delete {student.name}?</div>
      <div className="mt-1 text-[13.5px] text-slate-500">
        {student.ref} · {student.school || 'No school on file'}
      </div>
      <div className="mt-4 rounded-xl bg-red-50 px-3 py-[10px] text-[13px] leading-normal text-red-800">
        {first} will disappear from the student list, groups and check-in, and their ID card will stop
        working. Their past visits and payments stay in your Excel reports.
        {student.owes && ' Their unpaid fee stays on the payments report too.'}
      </div>
      {!online && (
        <div className="mt-3 text-[12.5px] text-slate-500">You're offline. Connect to delete a student.</div>
      )}
      <PrimaryButton
        className="mt-4"
        style={{ background: '#dc2626' }}
        disabled={!online}
        loading={remove.isPending}
        onClick={() => remove.mutate()}
      >
        Delete {first}
      </PrimaryButton>
      <button
        type="button"
        onClick={onClose}
        className="mt-2 w-full cursor-pointer border-none bg-transparent py-2 text-[13.5px] font-semibold text-slate-500"
      >
        Keep {first}
      </button>
    </BottomSheet>
  );
}
